/**
 * A month of results, one cell per day.
 *
 * A curve answers "which way over the period". A calendar answers "which days"
 * — which is the question an operator actually has when something looks wrong,
 * because a bad Tuesday and a slow month look identical on a line.
 *
 * Every cell comes measured from the server. The browser groups nothing and
 * sums nothing: reading a day off the cumulative curve would mean subtracting
 * two points, and a screen doing arithmetic on money is a second opinion about
 * the ledger.
 *
 * A day with no closed cycle is not a zero. It reads as empty, and the two are
 * drawn differently, because "flat" and "nothing happened" are different facts
 * and only one of them is a result.
 */
import { useState } from "react";
import { Button } from "@/design-system/Button";

import type { DailyResultResponse } from "@/shared/api";
import { isNegative, isZero, signedMoney, toPlaces } from "@/shared/money/money";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"] as const;

type Cell =
  | { readonly kind: "blank" }
  | { readonly kind: "day"; readonly day: number; readonly result: DailyResultResponse | null };

/** Monday-first index, because the week starts on Monday for this reader. */
function weekday(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function monthKey(date: Date): string {
  return `${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function grid(year: number, month: number, results: readonly DailyResultResponse[]): Cell[] {
  const byDay = new Map<number, DailyResultResponse>();
  for (const result of results) {
    const on = new Date(result.on);
    if (on.getUTCFullYear() === year && on.getUTCMonth() === month) {
      byDay.set(on.getUTCDate(), result);
    }
  }

  const first = new Date(Date.UTC(year, month, 1));
  const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: Cell[] = Array.from({ length: weekday(first) }, () => ({ kind: "blank" }));
  for (let day = 1; day <= length; day += 1) {
    cells.push({ kind: "day", day, result: byDay.get(day) ?? null });
  }
  return cells;
}

function tone(result: DailyResultResponse | null): string {
  if (result === null) return "border-border-subtle text-text-muted";
  const shown = toPlaces(result.net);
  if (isZero(shown)) return "border-border-subtle text-text-secondary";
  return isNegative(shown)
    ? "border-status-negative/40 bg-status-negative/10 text-status-negative"
    : "border-status-positive/40 bg-status-positive/10 text-status-positive";
}

export function PnlCalendar({
  daily,
  loading,
}: {
  readonly daily: readonly DailyResultResponse[];
  readonly loading: boolean;
}): React.JSX.Element {
  // Opens on the month of the most recent result rather than on today: a
  // period ending last week would otherwise open on an empty grid.
  const latest = daily.at(-1);
  const [shown, setShown] = useState<Date>(() =>
    latest === undefined ? new Date() : new Date(latest.on),
  );

  const year = shown.getUTCFullYear();
  const month = shown.getUTCMonth();
  const cells = grid(year, month, daily);

  const inMonth = daily.filter((result) => {
    const on = new Date(result.on);
    return on.getUTCFullYear() === year && on.getUTCMonth() === month;
  });

  const step = (months: number): void => {
    setShown(new Date(Date.UTC(year, month + months, 1)));
  };

  return (
    <section
      data-testid="pnl-calendar"
      className="min-w-0 overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={`${PANEL_HEADER} flex-wrap justify-between gap-x-3 gap-y-1`}>
        <h2 className={PANEL_TITLE}>Суточный PnL</h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Предыдущий месяц"
            data-testid="calendar-previous"
            onClick={() => {
              step(-1);
            }}
            className="size-6 text-text-muted"
          >
            ‹
          </Button>
          <span
            data-testid="calendar-month"
            className="min-w-20 text-center text-helper-sm tabular-nums text-text-secondary"
          >
            {monthKey(shown)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Следующий месяц"
            data-testid="calendar-next"
            onClick={() => {
              step(1);
            }}
            className="size-6 text-text-muted"
          >
            ›
          </Button>
        </div>
      </div>

      <div className="p-3">
        {loading ? (
          <p className="text-helper-sm text-text-muted">Читаю дни…</p>
        ) : (
          <>
            <div className="mb-2 grid grid-cols-7 gap-1">
              {WEEKDAYS.map((name) => (
                <span key={name} className="text-center text-label-caps text-text-muted">
                  {name}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell, index) =>
                cell.kind === "blank" ? (
                  <span key={`blank-${String(index)}`} />
                ) : (
                  <span
                    key={cell.day}
                    data-testid={`calendar-day-${String(cell.day)}`}
                    data-empty={cell.result === null ? "true" : "false"}
                    title={
                      cell.result === null
                        ? "Закрытых циклов не было"
                        : `${String(cell.result.cycles)} цикл(ов)`
                    }
                    className={`flex min-h-11 flex-col justify-between rounded-row border px-1.5 py-1 ${tone(cell.result)}`}
                  >
                    <span className="text-label-caps tabular-nums">{cell.day}</span>
                    {/* The sign carries the result, not the colour: the fill is
                        a second cue, and one man in twelve cannot use it. */}
                    <span className="truncate text-helper-sm tabular-nums">
                      {cell.result === null ? "—" : signedMoney(cell.result.net)}
                    </span>
                  </span>
                ),
              )}
            </div>

            <p className="mt-2 text-helper-sm text-text-muted" data-testid="calendar-summary">
              {inMonth.length === 0
                ? "В этом месяце закрытых циклов нет"
                : `${String(inMonth.length)} торговых дней · прочерк — закрытых циклов не было`}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
